import { LoadingButton } from "@mui/lab";
import { Box, Button, Checkbox, DialogActions, DialogContent, DialogTitle, Divider, Stack, Typography } from "@mui/material";
import Dialog from '@mui/material/Dialog';
import { makeStyles } from "@mui/styles";
import { VirtualItem } from "@tanstack/react-virtual";
import { memo, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { DashboardsContext } from "../../../contexts/DashboardsContext";
import { WorkspaceContext } from "../../../contexts/WorkspaceContext";
import { WorkspacesContext } from "../../../contexts/WorkspacesContext";
import useBulkDashboards from "../../../hooks/useBulkDashboards";
import useDashboardsTable from "../../../hooks/useDashboardsTable";
import useVirtualTable from "../../../hooks/useVirtualTable";
import useWorkspaces from "../../../hooks/useWorkspaces";
import { scrollbarStyle } from "../../../shared/dashboard";
import theme from "../../../theme";
import { Dashboard, Workspace } from "../../../types/files";
import ArrayUtils from "../../../utils/ArrayUtils";
import NoDashboardsFoundView from "../../molecules/dashboards-empty/NoDashboardsFoundView";
import DashboardsSearchOrCreate from '../../molecules/dashboards-search-create/DashboardsSearchOrCreate';
import DashboardsTableHeader from "../../molecules/dashboards-table/DashboardsTableHeader";
import DashboardsTableRow from "../../molecules/dashboards-table/DashboardsTableRow";
import { TableHeaders } from "../../molecules/workspaces/SharedByAll";
import { collectionKey } from "../../pages/InvestorsPage";
import { FallbackLoading } from "../../templates/loader";
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';

const useStyles = makeStyles((theme) => ({
    dialog: {
        '& .MuiDialog-paper': {
            minWidth: 1430,
            minHeight: 800,
            maxHeight: 800,
            width: 'min-content',
            height: 'fit-content',
            padding: 20,
            borderRadius: 24,
        },
    },
    dialogTitle: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        padding: 'unset',
        color: 'black',
        fontWeight: 'bold',
        fontFamily: 'Inter',
        fontSize: '1.5rem',
    },
    dialogContent: {
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        padding: '8px 0',
        height: '100%',
        overflowY: 'auto',
    },
    dialogActions: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
    },
    selected :{
        fontWeight: 'bold',
        fontFamily: 'Inter',
        fontSize: '1rem',
        color: 'black',
    },
    headers: {
        width: '100%',
        height: '100%',
        overflowX: 'auto',
        overflowY: 'hidden',
        scrollBehavior: 'smooth',
        ...scrollbarStyle,
    },
    //
    table: {
        width: '100%',
        height: 'calc(100vh - 120px)',
    },
    header: {
        width: '100%',
        height: '100%',
        overflowX: 'auto',
        overflowY: 'hidden',
        scrollBehavior: 'auto',
        borderBottom: `1px solid ${theme.colors.neutral['400']}`,
        ...scrollbarStyle,
    },
    rows: {
        position: 'relative',
        minWidth: '100%',
        width: 'fit-content',
        height: '100%',
        overflow: 'auto',
        scrollBehavior: 'smooth',
        ...scrollbarStyle,
    },
    row: {
        position: 'absolute',
        top: 0,
        left: 0,
        minWidth: '100%',
        width: 'fit-content',
        height: 'auto',
        borderTop: `1px solid ${theme.colors.neutral['400']}`,
        background: `#fff`,
        "&:hover": {
            background: `rgb(246, 251, 251, 0.8)`,
        },
    },
    cancelButton: {
        width: 100,
        borderRadius: 40,
        background: theme.colors.neutral['100'],
        color: theme.colors.neutral['600'],
        textTransform: 'none',
        fontWeight: 'bold',
        transition: 'ease-in-out 300ms',
    },
    saveButton: {
        width: 100,
        borderRadius: 20,
        background: theme.palette.primary.main,
        color: 'white',
        textTransform: 'none',
        fontWeight: 'bold',
        transition: 'ease-in-out 300ms',
    },
    loadingText: {
        fontFamily: 'Inter',
        fontSize: '1.2rem',
        fontWeight: 700,
        color: theme.colors.neutral['900'],
    },
    loadingTextSub: {
        fontFamily: 'Inter',
        fontSize: '0.9rem',
        fontWeight: 500,
        color: '#9e9e9e',
    },
    loading: {
        width: '100%',
        height: '100%',
        color: theme.palette.primary.main,
    },
    checkBox: {
        padding: 'unset !important',
        marginRight: '4px !important',
        "& > svg": {
            width: '20px !important',
            height: '20px !important',
        }
    },
    checkBoxText: {
        fontFamily: 'Inter',
        fontSize: '0.9rem',
        fontWeight: 'bold',
        color: theme.colors.neutral['700'],
    },
    createButton: {
        width: '18ch',
        height: 40,
        borderRadius: 32,
        borderColor: theme.colors.neutral['100'],
        background: '#E0F1F1',
        color: theme.palette.primary.main,
        fontSize: '1rem',
        fontWeight: 'bold',
        textTransform: 'none',
        transition: 'ease-in-out 300ms',
    },
}));

const AddCompaniesToInvestorModal: React.FC<{
    isOpen: boolean,
    workspace?: Workspace,
    onOpenModal?: (field: string) => void,
    onClose: () => void,
}> = ({ isOpen, workspace, onOpenModal, onClose }) => {
    const classes = useStyles();
    const { setWorkspaceScores } = useContext(WorkspacesContext);
    const { dashboards, externalDashboards, setExternalDashboards } = useContext(DashboardsContext);
    const { workspace: globalWorkspace } = useContext(WorkspaceContext);
    const { refreshScores, computeWorkspaceScores } = useWorkspaces();
    const { getBulkDashboards, assignToWorkspaces, addToBulk, clearBulk} = useBulkDashboards();

    const [saving, setSaving] = useState<boolean>(false);
    const [scoring, setScoring] = useState<boolean>(false);
    const [allChecked, setAllChecked] = useState<boolean>(false);
    const [search, setSearch] = useState<string>('');
    const timerRef = useRef<string | number | NodeJS.Timeout | undefined>(undefined);

    const { loading, rows, filters, handleFilterRows } = useDashboardsTable(undefined, search);
    const { allRows, rowVirtualizer, hasNextPage, virtualRowsRef, virtualHeaderRef }
        = useVirtualTable({ key: 'add-companies-to-investor-modal-166', source: rows, limit: 20, itemHeight: 60 });

    const localWorkspace = useMemo(() => workspace ?? globalWorkspace, [globalWorkspace, workspace]);

    const columns = useMemo(() => TableHeaders.filter(column => !['Fit score', 'Request Intro'].includes(column)), []);

    const bulkCount = useMemo(() => getBulkDashboards(collectionKey).length, [getBulkDashboards]);

    const handleRefresh = useCallback(() => {
        if (!!localWorkspace)
            refreshScores([localWorkspace], dashboards, () => setScoring(true));
    }, [localWorkspace, refreshScores, dashboards]);

    const handleAssigntoWorkspace = useCallback(() => {
        setSaving(true);
        const dashboardsToAssign = getBulkDashboards(collectionKey)?.map(({dashboard}) => dashboard);
        let newDashboards: Dashboard[] = [...externalDashboards];

        dashboardsToAssign.forEach(dashboard => {
            if (!!newDashboards.find(d => d.id === dashboard?.id))
                newDashboards = newDashboards.map(d => d.id === dashboard?.id ? ({...dashboard}) : d);
            else
                newDashboards = [{...dashboard}, ...newDashboards];
        });
        setExternalDashboards(ArrayUtils.sortByDescending(newDashboards, 'createdAt'));

        assignToWorkspaces(collectionKey, [localWorkspace!.id]).finally(() => {
            setSaving(false);
            onClose();
        });
    }, [assignToWorkspaces, externalDashboards, getBulkDashboards, localWorkspace, onClose, setExternalDashboards]);

    useEffect(() => {
        if (allChecked)
            Promise.all(rows.map(dashboard => Promise.resolve(addToBulk(collectionKey, dashboard))));
        else
            clearBulk();
    // eslint-disable-next-line
    }, [allChecked]);

    useEffect(() => {
        if (rows.length > bulkCount)
            setAllChecked(false);
    // eslint-disable-next-line
    }, [bulkCount]);

    useEffect(() => {
        setSaving(false);
        setScoring(false);
        clearBulk(collectionKey);
    // eslint-disable-next-line
    }, []);

    useEffect(() => {
        if (!!localWorkspace && scoring) {
           timerRef.current = setTimeout(() => {
               clearInterval(timerRef.current);
               computeWorkspaceScores([localWorkspace]).then((updatedWorkspaceScores) => {
                   setWorkspaceScores(updatedWorkspaceScores);
                }).finally(() => setScoring(false));
           }, 3000);
       }

       return () => {
           clearTimeout(timerRef.current);
       }
    // eslint-disable-next-line
    }, [scoring]);

    return (<>
        <Dialog className={classes.dialog} open={isOpen} onClose={!loading && !scoring ? onClose : undefined}>
            <DialogTitle className={classes.dialogTitle}>
                <Stack direction="row" alignItems="center" justifyContent="space-between" width="100%">
                    <Stack direction="row" alignItems="center" justifyContent="flex-start" width="100%">
                        <Box minWidth="400px">
                            <span>{`Add companies to ${localWorkspace?.name}`}</span>
                        </Box>
                        <Box minWidth="400px" maxWidth="800px">
                            <DashboardsSearchOrCreate search mediumFit onSearch={(value) => setSearch(value)} />
                        </Box>
                    </Stack>
                    <Stack direction="row" alignItems="center" justifyContent="flex-end" width="100%">
                        <Button variant="outlined"
                            className={classes.createButton}
                            startIcon={<AddCircleOutlineIcon />}
                            onClick={(e) => {
                                e.stopPropagation();
                                e.preventDefault();
                                onOpenModal?.('Add company');
                            }} disableRipple>{'Add company'} </Button>
                    </Stack>
                </Stack>
            </DialogTitle>
            <Divider sx={{ margin: '8px 0', marginTop: '12px' }} />
            <DialogContent className={classes.dialogContent}>
                <Stack className={classes.table}>
                    {loading ? (<FallbackLoading />) : (<>
                        <Stack alignItems="flex-start" justifyContent="flex-start" height="100%">
                            <Box width="100%" height="fit-content" overflow="hidden">
                                <Box className={classes.header} ref={virtualHeaderRef}>
                                    <DashboardsTableHeader
                                        columns={columns}
                                        filters={filters}
                                        onFilter={handleFilterRows}
                                        onRefreshScores={handleRefresh}
                                        noStatus />
                                </Box>
                            </Box>
                            {!!rows?.length ? (<>
                                <Box width="100%" height="100%" overflow="hidden">
                                    <Box className={classes.rows} ref={virtualRowsRef}
                                        sx={{ height: `${rowVirtualizer.getTotalSize()}px` }}>
                                        {rowVirtualizer.getVirtualItems().map((virtualRow: VirtualItem) => (
                                            <Box className={classes.row}
                                                sx={{  height: `${virtualRow.size}px`, transform: `translateY(${virtualRow.start}px)`, }}
                                                key={'add-companies-to-investor-modal-289-' + virtualRow.index} >
                                                {(virtualRow.index > allRows.length - 1) && hasNextPage
                                                    ? (<FallbackLoading />)
                                                    : (<DashboardsTableRow
                                                            dashboard={allRows[virtualRow.index]}
                                                            workspace={workspace ?? undefined}
                                                            columns={columns}
                                                            collectionKey={collectionKey}
                                                            refreshingScore={scoring}
                                                            isPublic noStatus selection />)}
                                            </Box>
                                        ))}
                                    </Box>
                                </Box>
                            </>) : (
                                <Stack alignItems="flex-start" justifyContent="flex-start" width="100%" height="100%">
                                    <Divider sx={{ width: '100%' }} />
                                    <Stack className={classes.rows} alignItems="flex-start" justifyContent="flex-start">
                                        <NoDashboardsFoundView
                                            noDataYetModal={!dashboards.length}
                                            noCompaniesYet={!search && !filters?.stages?.length}
                                            noCompaniesFound />
                                    </Stack>
                                </Stack>
                            )}
                        </Stack>
                    </>)}
                </Stack>
            </DialogContent>
            <DialogActions className={classes.dialogActions}>
                <Stack direction="row" alignItems="center" justifyContent="flex-start" width="100%"
                    sx={{ transform: 'translateX(-10px)'}}>
                    <Checkbox edge="end"
                        className={classes.checkBox}
                        checked={allChecked}
                        onClick={(e: any) => {
                            e.stopPropagation();
                            e.preventDefault();
                            setAllChecked(prev => !prev);
                        }}
                        disableRipple
                    />
                    <Typography className={classes.checkBoxText}>
                        {'Select all'}&nbsp;
                        <Typography component="span" className={classes.checkBoxText}
                            color={`${theme.colors.neutral['600']} !important`} fontWeight="500 !important" display="inline">
                            {`(${rows.length} compan${rows.length > 1 ? 'ies': 'y'})`}
                        </Typography>
                    </Typography>
                </Stack>
                <Stack direction="row" spacing={2} alignItems="center" justifyContent="flex-end" width="100%">
                    {!scoring && (
                        <Typography className={classes.selected}>
                            {`${bulkCount} compan${bulkCount > 1 ? 'ies': 'y'} selected`}
                        </Typography>
                    )}
                    <Button className={classes.cancelButton}
                        disabled={scoring}
                        onClick={onClose}> {'Cancel'} </Button>
                    <LoadingButton variant="contained"
                        className={classes.saveButton}
                        loading={saving}
                        disabled={scoring}
                        onClick={handleAssigntoWorkspace}
                    > {'Add'} </LoadingButton>
                </Stack>
            </DialogActions>
        </Dialog>
    </>);
}

export default memo(AddCompaniesToInvestorModal);
